import React, { useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { PHONES } from '../../lib/vocalTract/articulations';
import { postureFrame } from '../../lib/samuel/phonetic';
import { startVoiceTracker } from '../../lib/vocalTract/voiceTracker';

// IPA vowel trapezoid (x: front→back, y: close→open), with RP / GA symbols.
const VOWELS = [
  ['iː', 0.08, 0.06, 'FLEECE'], ['ɪ', 0.22, 0.2, 'KIT'], ['e', 0.18, 0.44, 'DRESS'], ['ɛ', 0.24, 0.56, 'DRESS GA'],
  ['æ', 0.3, 0.86, 'TRAP'], ['ʌ', 0.6, 0.72, 'STRUT'], ['ɑː', 0.8, 0.94, 'PALM'], ['ɒ', 0.9, 0.9, 'LOT'],
  ['ɔː', 0.88, 0.54, 'THOUGHT'], ['ʊ', 0.76, 0.2, 'FOOT'], ['uː', 0.94, 0.06, 'GOOSE'], ['ɜː', 0.56, 0.4, 'NURSE'],
  ['ə', 0.52, 0.6, 'schwa'], ['ɝ', 0.4, 0.36, 'NURSE GA'], ['ɚ', 0.36, 0.66, 'letter GA'],
];
const X = (x, y) => 40 + y * 120 + x * (440 - y * 120);
const Y = (y) => 30 + y * 260;
export const formantToXY = ({ f1, f2 }) => ({ x: Math.max(0, Math.min(1, (2500 - f2) / 1750)), y: Math.max(0, Math.min(1, (f1 - 250) / 650)) });

export const VowelTrapezoid = ({ lab, target, liveFormant }) => {
  const [drag, setDrag] = useState(null);
  const [mic, setMic] = useState(null);
  const stopRef = useRef(null);
  useEffect(() => () => stopRef.current?.(), []);
  const sound = (frame) => { lab.stopAll(); lab.eng.resume(); lab.eng.pose(frame, true); };
  const playVowel = (sym) => { const s = PHONES[sym]?.s; if (s) sound(postureFrame(s)); };
  const at = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const y = Math.max(0, Math.min(1, ((e.clientY - r.top) / r.height * 320 - 30) / 260));
    const x = Math.max(0, Math.min(1, ((e.clientX - r.left) / r.width * 520 - 40 - y * 120) / (440 - y * 120)));
    return { x, y };
  };
  const morph = ({ x, y }) => sound(postureFrame({ ti: 29 - x * 16.5, td: 2.05 + y * 1.4, c: x > 0.6 && y < 0.65 ? [[41, 0.6 + y * 0.6]] : [], voice: true, tenseness: 0.6 }));
  const toggleMic = async () => {
    if (mic) { stopRef.current?.(); stopRef.current = null; setMic(null); return; }
    setMic({}); lab.eng.resume();
    const stop = () => {};
    stopRef.current = stop;
    try {
      const s = await startVoiceTracker((f) => { if (f.f1 && f.f2) setMic(formantToXY(f)); });
      if (stopRef.current === stop) stopRef.current = s; else s();
    } catch { setMic(null); lab.setError('Microfono non disponibile'); }
  };
  const live = liveFormant ? formantToXY(liveFormant) : null;
  return (
    <div className="space-y-2 max-w-2xl" data-testid="vowel-trapezoid">
      <svg viewBox="0 0 520 320" className="w-full select-none touch-none rounded-xl bg-slate-950 border border-cyan-500/20 cursor-pointer"
        onPointerDown={(e) => { if (e.target.dataset.sym) return; e.currentTarget.setPointerCapture(e.pointerId); const p = at(e); setDrag(p); morph(p); }}
        onPointerMove={(e) => { if (!drag) return; const p = at(e); setDrag(p); morph(p); }}
        onPointerUp={() => { setDrag(null); lab.eng.endVoice(); }} data-testid="trapezoid-svg">
        <polygon points={`${X(0, 0)},${Y(0)} ${X(1, 0)},${Y(0)} ${X(1, 1)},${Y(1)} ${X(0, 1)},${Y(1)}`} fill="#0f172a" stroke="#22d3ee55" strokeWidth="2" />
        {[0.33, 0.66].map((y) => <line key={y} x1={X(0, y)} y1={Y(y)} x2={X(1, y)} y2={Y(y)} stroke="#22d3ee22" />)}
        <line x1={X(0.5, 0)} y1={Y(0)} x2={X(0.5, 1)} y2={Y(1)} stroke="#22d3ee22" />
        <text x="40" y="18" fill="#64748b" fontSize="11">anteriore</text><text x="440" y="18" fill="#64748b" fontSize="11">posteriore</text>
        <text x="486" y={Y(0) + 4} fill="#64748b" fontSize="10">chiusa</text><text x="486" y={Y(1) + 4} fill="#64748b" fontSize="10">aperta</text>
        {VOWELS.map(([s, x, y, w]) => {
          const on = target === s || target === s.replace('ː', '');
          return (
            <g key={s} onPointerDown={(e) => { e.stopPropagation(); playVowel(s); }} onPointerUp={() => lab.eng.endVoice()} data-testid={`trap-${s}`} className="cursor-pointer">
              <circle data-sym={s} cx={X(x, y)} cy={Y(y)} r={on ? 17 : 14} fill={on ? '#fbbf24' : '#155e75'} stroke={on ? '#fff' : '#22d3ee'} strokeWidth={on ? 2.5 : 1} />
              <text data-sym={s} x={X(x, y)} y={Y(y) + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill={on ? '#0f172a' : '#e0f2fe'}>{s}</text>
              <text x={X(x, y)} y={Y(y) + 27} textAnchor="middle" fontSize="8" fill="#64748b">{w}</text>
            </g>
          );
        })}
        {drag && <circle cx={X(drag.x, drag.y)} cy={Y(drag.y)} r="7" fill="#f472b6" />}
        {live && <circle cx={X(live.x, live.y)} cy={Y(live.y)} r="9" fill="none" stroke="#a3e635" strokeWidth="3" data-testid="trap-live-dot" />}
        {mic && mic.x !== undefined && <circle cx={X(mic.x, mic.y)} cy={Y(mic.y)} r="10" fill="#f472b6aa" stroke="#fff" data-testid="trap-mic-dot" />}
      </svg>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
        <span>Tocca un simbolo per sentirlo, trascina per passare da una vocale all’altra.</span>
        <span><span className="text-lime-400">○</span> clip attuale</span>
        <button onClick={toggleMic} data-testid="trap-mic-toggle" className={`ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full font-semibold ${mic ? 'bg-pink-500 text-white' : 'bg-slate-800 text-cyan-100'}`}><Mic className="w-3 h-3" /> {mic ? 'Ferma' : 'Segui la mia voce'}</button>
      </div>
    </div>
  );
};
